import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Snackbar } from '@mui/material'
import { Search, MenuBook, Link as LinkIcon } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageLayout, PageHeader } from '../../components'
import styles from './Glossary.module.scss'

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

const GlossaryPage = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeLetter, setActiveLetter] = useState(null)
  const [copiedSnack, setCopiedSnack] = useState(false)
  const contentRef = useRef(null)
  const letterRefs = useRef({})
  const highlightTimeout = useRef(null)

  const glossaryTerms = t('glossary.terms', { returnObjects: true })

  const { grouped, totalCount } = useMemo(() => {
    const terms = Object.entries(glossaryTerms)
      .filter(([termKey, term]) => {
        const s = searchTerm.toLowerCase()
        return (
          termKey.toLowerCase().includes(s) ||
          term.title.toLowerCase().includes(s) ||
          term.description.toLowerCase().includes(s)
        )
      })
      .map(([key, term]) => ({ key, title: term.title, description: term.description }))
      .sort((a, b) => a.title.localeCompare(b.title))

    const grp = terms.reduce((acc, term) => {
      const letter = term.title[0].toUpperCase()
      if (!acc[letter]) acc[letter] = []
      acc[letter].push(term)
      return acc
    }, {})

    return { grouped: grp, totalCount: terms.length }
  }, [searchTerm, glossaryTerms])

  const sortedLetters = useMemo(() => Object.keys(grouped).sort(), [grouped])
  const activeLetterSet = useMemo(() => new Set(sortedLetters), [sortedLetters])

  useEffect(() => {
    if (!location.hash) return
    const id = location.hash.slice(1)
    const timer = setTimeout(() => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add(styles.cardHighlight)
        if (highlightTimeout.current) clearTimeout(highlightTimeout.current)
        highlightTimeout.current = setTimeout(() => {
          el.classList.remove(styles.cardHighlight)
        }, 2500)
      }
    }, 150)
    return () => clearTimeout(timer)
  }, [location.hash])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveLetter(entry.target.dataset.letter)
          }
        }
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
    )

    sortedLetters.forEach((letter) => {
      const el = letterRefs.current[letter]
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sortedLetters])

  const scrollToLetter = useCallback((letter) => {
    const el = letterRefs.current[letter]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const copyTermLink = useCallback((termKey) => {
    const url = `${window.location.origin}${window.location.pathname}#${termKey}`
    navigator.clipboard.writeText(url).then(() => {
      navigate({ hash: termKey }, { replace: true })
      setCopiedSnack(true)
    })
  }, [navigate])

  return (
    <PageLayout>
      <PageHeader
        title={t('glossary.title')}
        subtitle={t('glossary.subtitle')}
      >
        <div className={styles.searchBar}>
          <div className={styles.searchContainer}>
            <Search className={styles.searchIcon} />
            <input
              type='text'
              placeholder='Search terms...'
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <span className={styles.searchCount}>
                {totalCount} {totalCount === 1 ? 'result' : 'results'}
              </span>
            )}
          </div>
        </div>
      </PageHeader>

      <div className={styles.glossaryLayout}>
        {/* Sticky sidebar index */}
        <nav className={styles.sidebar} aria-label='Glossary index'>
          <div className={styles.sidebarInner}>
            {ALPHABET.map((letter) => {
              const isActive = activeLetterSet.has(letter)
              const isCurrent = activeLetter === letter
              return (
                <button
                  key={letter}
                  className={[
                    styles.sidebarLetter,
                    isActive ? styles.sidebarLetterActive : '',
                    isCurrent ? styles.sidebarLetterCurrent : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => isActive && scrollToLetter(letter)}
                  type='button'
                  tabIndex={isActive ? 0 : -1}
                  aria-label={`Jump to ${letter}`}
                >
                  {letter}
                </button>
              )
            })}
          </div>
        </nav>

        {/* Main content area */}
        <div className={styles.content} ref={contentRef}>
          {sortedLetters.length === 0 && (
            <div className={styles.emptyState}>
              <MenuBook className={styles.emptyIcon} />
              <Typography variant='h6' className={styles.emptyTitle}>
                No terms found
              </Typography>
              <Typography variant='body2' className={styles.emptyText}>
                Try adjusting your search to find what you are looking for.
              </Typography>
            </div>
          )}

          {sortedLetters.map((letter) => (
            <section
              key={letter}
              className={styles.letterSection}
              data-letter={letter}
              ref={(el) => { letterRefs.current[letter] = el }}
            >
              <div className={styles.letterHeader}>
                <span className={styles.letterAccent}>{letter}</span>
                <span className={styles.letterCount}>
                  {grouped[letter].length}
                  {' '}
                  {grouped[letter].length === 1 ? 'term' : 'terms'}
                </span>
              </div>

              <div className={styles.termsGrid}>
                {grouped[letter].map((term) => (
                  <button
                    key={term.key}
                    id={term.key}
                    type='button'
                    className={styles.termCard}
                    onClick={() => copyTermLink(term.key)}
                    title='Click to copy link'
                  >
                    <div className={styles.termHeader}>
                      <Typography variant='h6' className={styles.termTitle}>
                        {term.title}
                      </Typography>
                      <LinkIcon className={styles.anchorIcon} />
                    </div>
                    <Typography className={styles.termDefinition} component='p'>
                      {term.description}
                    </Typography>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <Snackbar
        open={copiedSnack}
        autoHideDuration={2000}
        onClose={() => setCopiedSnack(false)}
        message='Link copied to clipboard'
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageLayout>
  )
}

export default GlossaryPage
